import { TourAnalytics, TourAnalyticsPatch, TourState } from '../types';

/** Every field that lives on the server. Explicit, so a new field is a deliberate addition. */
const ANALYTICS_FIELDS: (keyof TourAnalytics)[] = [
  'currentStepId',
  'furthestStepId',
  'furthestStepNumber',
  'startedAt',
  'lastStartedAt',
  'skippedAt',
  'completedAt',
  'lastCompletedAt',
  'completedTimes',
];

/**
 * Build the server payload: the durable analytics only.
 *
 * `stepHistory` is deliberately dropped — it is the one bulky field and it stays client-side
 * (see the hybrid storage decision in docs/adr/0001). Everything else is small and belongs on
 * the user object so progress is legible across devices.
 *
 * Callers must pass the MERGED view (selectTourGate), never the raw client slice: the client
 * slice never absorbs `user.tours`, so uploading it would push this device's copy over a server
 * record that may be further ahead. The merge applies the monotonic guards, which is what makes
 * a sync incapable of regressing progress.
 */
export const toursSyncPayload = (tours: Record<string, TourState>): Record<string, TourAnalytics> => {
  const payload: Record<string, TourAnalytics> = {};
  Object.entries(tours ?? {}).forEach(([tourId, tour]) => {
    const analytics: Partial<TourState> = { ...tour };
    delete analytics.stepHistory; // the one bulky field; stays client-side
    payload[tourId] = analytics as TourAnalytics;
  });
  return payload;
};

/**
 * The PATCH body: per tour, only the fields that differ from what the server already holds.
 *
 * A field the client has cleared (restart wipes `completedAt`/`skippedAt`/`furthest*`) is sent
 * as `null` rather than omitted — omission means "leave it alone", so a clear would never land.
 * A tour with nothing to say is left out entirely, and an empty result means no request at all,
 * which is why idle navigation costs nothing.
 *
 * Restart is the interesting case: `completedTimes` is untouched across runs so it drops out on
 * its own, while `lastStartedAt`/`lastCompletedAt` DO change (they inherit the finished run's
 * values, which the server has no way to derive) and are therefore included.
 */
export const toursSyncDelta = (
  merged: Record<string, TourState>,
  baseline: Record<string, TourAnalytics> | undefined,
): Record<string, TourAnalyticsPatch> => {
  const delta: Record<string, TourAnalyticsPatch> = {};

  Object.entries(merged ?? {}).forEach(([tourId, tour]) => {
    const stored = baseline?.[tourId];
    const changes: Record<string, unknown> = {};

    ANALYTICS_FIELDS.forEach((field) => {
      const next = tour[field];
      const prev = stored?.[field];
      if (next === prev) return;
      // Absent locally but present on the server: an explicit clear, not an omission.
      if (next === undefined) {
        if (prev !== undefined && prev !== null) changes[field] = null;
        return;
      }
      changes[field] = next;
    });

    if (Object.keys(changes).length > 0) delta[tourId] = changes as TourAnalyticsPatch;
  });

  return delta;
};

export default toursSyncPayload;
